/**
 * Search utility functions for multi-way search across the library system.
 * Supports searching by book name, borrower name, and all other relevant fields.
 */

/**
 * Normalizes a string for case-insensitive comparison
 */
export const normalizeSearchTerm = (term: string | null | undefined): string => {
  return (term || '').toLowerCase().trim();
};

/**
 * Checks if a search term matches any of the provided fields
 */
export const matchesSearch = (searchTerm: string, ...fields: (string | null | undefined)[]): boolean => {
  if (!searchTerm) return true;

  const normalizedSearch = normalizeSearchTerm(searchTerm);
  return fields.some(field => {
    if (!field) return false;
    return normalizeSearchTerm(field).includes(normalizedSearch);
  });
};

/**
 * Comprehensive book search - searches by title, author, and borrower name
 */
export const searchBook = (
  searchTerm: string,
  book: {
    title?: string;
    author_name?: string;
  },
  borrowerName?: string
): boolean => {
  if (!searchTerm) return true;

  const { title, author_name } = book;

  // Search in title, author, and borrower name
  return matchesSearch(
    searchTerm,
    title,
    author_name,
    borrowerName
  );
};

/**
 * Book record search - searches across borrower and book information
 */
export const searchBookRecord = (
  searchTerm: string,
  record: {
    borrower_name?: string;
    contact_info?: string | null;
    book_title?: string;
    book_sn?: string;
    book_id?: number | string;
    borrowed_date?: string;
    return_date?: string | null;
    notes?: string | null;
  },
  bookDetails?: {
    title?: string;
    book_id?: string;
    author_name?: string;
  }
): boolean => {
  if (!searchTerm) return true;

  const { 
    borrower_name, 
    contact_info, 
    book_title, 
    book_sn, 
    book_id,
    borrowed_date,
    return_date,
    notes 
  } = record;

  // Include book details if provided
  const bookTitle = bookDetails?.title || book_title;
  const bookId = bookDetails?.book_id || String(book_id);
  const authorName = bookDetails?.author_name;

  return matchesSearch(
    searchTerm,
    borrower_name,
    contact_info,
    bookTitle,
    book_sn,
    bookId,
    authorName,
    borrowed_date,
    return_date,
    notes
  );
};

/**
 * Formats a date string for search comparison
 */
export const formatDateForSearch = (dateString: string | null | undefined): string => {
  if (!dateString) return '';
  
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).toLowerCase();
  } catch {
    return '';
  }
};

/**
 * Advanced multi-field search for book records with book details
 */
export const searchBookRecordAdvanced = (
  searchTerm: string,
  record: {
    borrower_name: string;
    contact_info?: string | null;
    book_title?: string;
    book_sn?: string;
    book_id: number;
    borrowed_date: string;
    return_date?: string | null;
    notes?: string | null;
    status?: string;
  },
  book?: {
    title?: string;
    book_id?: string;
    author_name?: string;
    isbn?: string | null;
    category?: string | null;
  }
): boolean => {
  if (!searchTerm) return true;

  const normalizedSearch = normalizeSearchTerm(searchTerm);
  const dateSearch = formatDateForSearch(record.borrowed_date);
  const returnDateSearch = formatDateForSearch(record.return_date);

  // Search in record fields
  const recordFields = [
    record.borrower_name,
    record.contact_info,
    record.book_title,
    record.book_sn,
    String(record.book_id),
    dateSearch,
    returnDateSearch,
    record.notes,
    record.status,
  ];

  // Search in book fields if available
  const bookFields = book ? [
    book.title,
    book.book_id,
    book.author_name,
    book.isbn,
    book.category,
  ] : [];

  return matchesSearch(searchTerm, ...recordFields, ...bookFields);
};
